
import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { UnitCombobox } from "@/components/ui/unit-combobox"
import { useCreatePackage, useCreateMultiplePackages } from "@/hooks/usePackages"
import { useUnits } from "@/hooks/useUnits"
import { useAuth } from "@/hooks/useAuth"
import { supabase } from "@/integrations/supabase/client"
import { Camera, Loader2, Package, Plus, Trash2 } from "lucide-react"
import { toast } from "sonner"

interface RegistrarEncomendaModalProps {
  children?: React.ReactNode
  triggerButton?: React.ReactNode
}

interface PackageForm {
  id: string
  recipientName: string
  company: string
  description: string
  whatsappPhone: string
  photo: File | null
  photoPreview?: string
}

const RegistrarEncomendaModal = ({ children, triggerButton }: RegistrarEncomendaModalProps) => {
  const [open, setOpen] = useState(false)
  const [selectedUnit, setSelectedUnit] = useState("")
  const [packages, setPackages] = useState<PackageForm[]>([
    {
      id: crypto.randomUUID(),
      recipientName: "",
      company: "",
      description: "",
      whatsappPhone: "",
      photo: null
    }
  ])
  const [uploading, setUploading] = useState(false)
  const [processing, setProcessing] = useState(false)

  const { mutate: createPackage } = useCreatePackage()
  const { mutate: createMultiplePackages } = useCreateMultiplePackages()
  const { data: units } = useUnits()
  const { profile } = useAuth()

  const addPackage = () => {
    setPackages([...packages, {
      id: crypto.randomUUID(),
      recipientName: "",
      company: "",
      description: "",
      whatsappPhone: "",
      photo: null
    }])
  }

  const removePackage = (id: string) => {
    if (packages.length > 1) {
      // Encontrar pacote a ser removido
      const pkgToRemove = packages.find(pkg => pkg.id === id)
      
      // Liberar preview se existir
      if (pkgToRemove?.photoPreview) {
        URL.revokeObjectURL(pkgToRemove.photoPreview)
      }
      
      // Limpar referência da foto
      if (pkgToRemove?.photo) {
        pkgToRemove.photo = null
      }
      
      setPackages(packages.filter(pkg => pkg.id !== id))
      toast.success("Encomenda removida")
    }
  }

  const updatePackage = (id: string, field: keyof PackageForm, value: string | File | null) => {
    setPackages(packages.map(pkg => 
      pkg.id === id ? { ...pkg, [field]: value } : pkg
    ))
  }

  const compressImage = async (file: File): Promise<File> => {
    return new Promise((resolve, reject) => {
      // Verificar tamanho do arquivo original
      if (file.size > 10 * 1024 * 1024) { // 10MB
        toast.error("Foto muito grande! Máximo 10MB")
        reject(new Error("File too large"))
        return
      }

      const reader = new FileReader()
      
      reader.onerror = () => {
        reject(new Error("Erro ao ler arquivo"))
      }
      
      reader.onload = (event) => {
        const img = new Image()
        
        img.onerror = () => {
          reject(new Error("Erro ao carregar imagem"))
        }
        
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas')
            const MAX_WIDTH = 800
            const MAX_HEIGHT = 800
            let width = img.width
            let height = img.height

            // Calcular novas dimensões
            if (width > height) {
              if (width > MAX_WIDTH) {
                height = Math.round((height * MAX_WIDTH) / width)
                width = MAX_WIDTH
              }
            } else {
              if (height > MAX_HEIGHT) {
                width = Math.round((width * MAX_HEIGHT) / height)
                height = MAX_HEIGHT
              }
            }

            canvas.width = width
            canvas.height = height
            const ctx = canvas.getContext('2d', { 
              alpha: false,
              willReadFrequently: false 
            })
            
            if (!ctx) {
              reject(new Error("Erro ao criar canvas"))
              return
            }

            ctx.imageSmoothingEnabled = true
            ctx.imageSmoothingQuality = 'medium'
            ctx.drawImage(img, 0, 0, width, height)

            // Limpar referência da imagem IMEDIATAMENTE
            img.src = ''
            img.onload = null
            img.onerror = null

            canvas.toBlob((blob) => {
              // Limpar canvas IMEDIATAMENTE
              ctx.clearRect(0, 0, canvas.width, canvas.height)
              canvas.width = 0
              canvas.height = 0
              
              if (blob) {
                const compressedFile = new File([blob], file.name, { 
                  type: 'image/jpeg',
                  lastModified: Date.now()
                })
                
                console.log(`Foto comprimida: ${(file.size / 1024).toFixed(0)}KB → ${(compressedFile.size / 1024).toFixed(0)}KB`)
                
                resolve(compressedFile)
              } else {
                reject(new Error("Erro ao comprimir imagem"))
              }
            }, 'image/jpeg', 0.75)
            
          } catch (error) {
            reject(error)
          }
        }
        
        img.src = event.target?.result as string
      }
      
      reader.readAsDataURL(file)
    })
  }

  const handlePhotoCapture = async (event: React.ChangeEvent<HTMLInputElement>, packageId: string) => {
    const file = event.target.files?.[0]
    if (!file) return

    const loadingToast = toast.loading("Otimizando foto...")
    
    try {
      const compressedFile = await compressImage(file)
      updatePackage(packageId, 'photo', compressedFile)
      toast.dismiss(loadingToast)
      toast.success(`Foto otimizada! ${(compressedFile.size / 1024).toFixed(0)}KB`)
    } catch (error) {
      console.error('Erro ao comprimir imagem:', error)
      toast.dismiss(loadingToast)
      toast.error('Erro ao processar foto. Tente novamente.')
      return
    } finally {
      // LIMPAR INPUT IMEDIATAMENTE
      event.target.value = ''
    }
  }

  const uploadPhoto = async (file: File): Promise<string | null> => {
    try {
      setUploading(true)
      const fileName = `packages/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`
      
      const { data, error } = await supabase.storage
        .from('packages')
        .upload(fileName, file)

      if (error) throw error

      const { data: { publicUrl } } = supabase.storage
        .from('packages')
        .getPublicUrl(fileName)

      console.log("Foto enviada com sucesso:", publicUrl)
      return publicUrl
    } catch (error) {
      console.error('Erro ao fazer upload da foto:', error)
      toast.error('Erro ao fazer upload da foto')
      return null
    } finally {
      setUploading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!selectedUnit) {
      toast.error("Por favor, selecione uma unidade usando a busca acima")
      return
    }

    if (!profile?.id) {
      toast.error("Usuário não autenticado")
      return
    }

    const hasValidPackage = packages.some(pkg => pkg.recipientName.trim())
    if (!hasValidPackage) {
      toast.error("Por favor, preencha pelo menos o nome do destinatário em uma encomenda")
      return
    }

    const validPackages = packages.filter(pkg => pkg.recipientName.trim())
    setProcessing(true)

    try {
      // Se há apenas uma encomenda, usar a função simples
      if (validPackages.length === 1) {
        const pkg = validPackages[0]
        let photoUrl = null
        
        if (pkg.photo) {
          console.log("Fazendo upload da foto...")
          photoUrl = await uploadPhoto(pkg.photo)
          if (!photoUrl) {
            toast.error(`Erro ao fazer upload da foto da encomenda`)
            setProcessing(false)
            return
          }
        }

        const packageData = {
          recipient_name: pkg.recipientName,
          sender: pkg.company || null,
          description: pkg.description || null,
          unit_id: selectedUnit,
          whatsapp_phone: pkg.whatsappPhone || null,
          photo_url: photoUrl,
          status: "pending" as const,
          received_at: new Date().toISOString(),
          received_by: profile.id,
          motoboy_id: null,
          delivered_at: null,
          notes: null,
          tracking_code: null
        }

        createPackage(packageData, {
          onSuccess: () => {
            // Limpar todas as referências
            packages.forEach(pkg => {
              if (pkg.photoPreview) {
                URL.revokeObjectURL(pkg.photoPreview)
              }
              if (pkg.photo) {
                pkg.photo = null
              }
            })
            
            setOpen(false)
            setSelectedUnit("")
            setPackages([{
              id: crypto.randomUUID(),
              recipientName: "",
              company: "",
              description: "",
              whatsappPhone: "",
              photo: null
            }])
            
            // Sugerir limpeza de cache do navegador (força GC)
            if ('gc' in window) {
              (window as any).gc()
            }
          }
        })
      } else {
        // Se há múltiplas encomendas, usar a função de múltiplas
        const processedPackages = []
        
        for (const pkg of validPackages) {
          let photoUrl = null
          if (pkg.photo) {
            console.log("Fazendo upload da foto...")
            photoUrl = await uploadPhoto(pkg.photo)
            if (!photoUrl) {
              toast.error(`Erro ao fazer upload da foto da encomenda`)
              continue
            }
          }

          const packageData = {
            recipient_name: pkg.recipientName,
            sender: pkg.company || null,
            description: pkg.description || null,
            unit_id: selectedUnit,
            whatsapp_phone: pkg.whatsappPhone || null,
            photo_url: photoUrl,
            received_by: profile.id,
            status: "pending" as const,
            received_at: new Date().toISOString(),
            motoboy_id: null,
            delivered_at: null,
            notes: null,
            tracking_code: null
          }

          processedPackages.push(packageData)
        }

        createMultiplePackages(processedPackages, {
          onSuccess: () => {
            // Limpar todas as referências
            packages.forEach(pkg => {
              if (pkg.photoPreview) {
                URL.revokeObjectURL(pkg.photoPreview)
              }
              if (pkg.photo) {
                pkg.photo = null
              }
            })
            
            setOpen(false)
            setSelectedUnit("")
            setPackages([{
              id: crypto.randomUUID(),
              recipientName: "",
              company: "",
              description: "",
              whatsappPhone: "",
              photo: null
            }])
            
            // Sugerir limpeza de cache do navegador (força GC)
            if ('gc' in window) {
              (window as any).gc()
            }
          }
        })
      }

    } catch (error) {
      console.error('Erro ao registrar encomendas:', error)
      toast.error('Erro ao registrar encomendas')
    } finally {
      setProcessing(false)
    }
  }

  // Use triggerButton if provided, otherwise use children, or fallback to default button
  const trigger = triggerButton || children || (
    <Button>
      <Package className="h-4 w-4 mr-2" />
      Registrar Encomenda
    </Button>
  )

  return (
    <Dialog 
      open={open} 
      onOpenChange={(newOpen) => {
        if (!newOpen) {
          // Limpar todas as fotos ao fechar
          packages.forEach(pkg => {
            if (pkg.photoPreview) {
              URL.revokeObjectURL(pkg.photoPreview)
            }
            if (pkg.photo) {
              pkg.photo = null
            }
          })
        }
        setOpen(newOpen)
      }}
    >
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Registrar Nova(s) Encomenda(s)</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="unit">Unidade *</Label>
            <UnitCombobox
              units={units || []}
              value={selectedUnit}
              onChange={setSelectedUnit}
              placeholder="Digite o número da unidade..."
              disabled={!units || units.length === 0}
            />
            {!units?.length && (
              <p className="text-xs text-muted-foreground">
                Carregando unidades...
              </p>
            )}
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-base font-semibold">Encomendas</Label>
              <Button type="button" variant="outline" size="sm" onClick={addPackage}>
                <Plus className="h-4 w-4 mr-1" />
                Adicionar Encomenda
              </Button>
            </div>

            {packages.map((pkg, index) => (
              <div key={pkg.id} className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="font-medium">Encomenda {index + 1}</Label>
                  {packages.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removePackage(pkg.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor={`recipient-${pkg.id}`}>Nome do Destinatário *</Label>
                    <Input
                      id={`recipient-${pkg.id}`}
                      value={pkg.recipientName}
                      onChange={(e) => updatePackage(pkg.id, 'recipientName', e.target.value)}
                      placeholder="Nome completo do morador"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`company-${pkg.id}`}>Empresa de Entrega</Label>
                    <Input
                      id={`company-${pkg.id}`}
                      value={pkg.company}
                      onChange={(e) => updatePackage(pkg.id, 'company', e.target.value)}
                      placeholder="Ex: Correios, Mercado Livre, Amazon"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`whatsapp-${pkg.id}`}>WhatsApp (opcional)</Label>
                    <Input
                      id={`whatsapp-${pkg.id}`}
                      value={pkg.whatsappPhone}
                      onChange={(e) => updatePackage(pkg.id, 'whatsappPhone', e.target.value)}
                      placeholder="(11) 99999-9999"
                    />
                  </div>

                   <div className="space-y-2">
                    <Label>Foto da Encomenda (opcional)</Label>
                    
                    {/* AVISO DE LIMITE */}
                    {packages.filter(p => p.photo).length >= 3 && !pkg.photo && (
                      <div className="text-xs text-amber-600 bg-amber-50 dark:bg-amber-950 p-2 rounded">
                        ⚠️ Limite recomendado: 3 fotos por vez em telefones básicos
                      </div>
                    )}
                    
                    <div className="flex items-center space-x-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => document.getElementById(`photo-input-${pkg.id}`)?.click()}
                        disabled={uploading || processing}
                      >
                        <Camera className="h-4 w-4 mr-2" />
                        {pkg.photo ? "Trocar Foto" : "Capturar Foto"}
                      </Button>
                      {pkg.photo && (
                        <span className="text-sm text-green-600">
                          ✓ {(pkg.photo.size / 1024).toFixed(0)}KB
                        </span>
                      )}
                    </div>
                    
                    {uploading && (
                      <div className="flex items-center justify-center p-2 bg-blue-50 dark:bg-blue-950 rounded">
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        <span className="text-sm">Otimizando foto...</span>
                      </div>
                    )}
                    
                    <input
                      id={`photo-input-${pkg.id}`}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={(e) => handlePhotoCapture(e, pkg.id)}
                      className="hidden"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor={`description-${pkg.id}`}>Número do Pedido/Observações</Label>
                  <Textarea
                    id={`description-${pkg.id}`}
                    value={pkg.description}
                    onChange={(e) => updatePackage(pkg.id, 'description', e.target.value)}
                    placeholder="Número do pedido, código de rastreamento ou observações"
                    rows={2}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={processing || uploading}>
              {(processing || uploading) && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {uploading ? "Enviando foto..." : `Registrar ${packages.filter(pkg => pkg.recipientName.trim()).length} Encomenda(s)`}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default RegistrarEncomendaModal
